import React, { useEffect, useState, useContext } from 'react';
import { AdminContext } from '../AdminContext';

export default function Registrations() {
  const { token } = useContext(AdminContext);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter registrations based on search term
  const filteredRegistrations = registrations.filter(reg =>
    reg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.track?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort registrations
  const sortedRegistrations = [...filteredRegistrations].sort((a, b) => {
    let aVal = a[sortConfig.key] || '';
    let bVal = b[sortConfig.key] || '';
    
    // Handle dates
    if (sortConfig.key === 'created_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (sortConfig.direction === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedRegistrations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRegistrations = sortedRegistrations.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  useEffect(() => {
    async function fetchRegistrations() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('/.netlify/functions/admin-list-registrations', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        let data;
        try {
          data = await res.json();
        } catch (jsonErr) {
          // Handle non-JSON responses (like HTML error pages)
          const text = await res.text();
          if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
            throw new Error('Server returned HTML instead of JSON. Please check if the backend is running correctly.');
          }
          throw new Error('Invalid JSON response from server');
        }
        
        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch registrations');
        }
        
        setRegistrations(data.registrations || []);
      } catch (err) {
        console.error('Error fetching registrations:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    if (token) fetchRegistrations();
  }, [token]);

  return (
    <div style={{ maxWidth: '100%', margin: '40px auto', padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 16 }}>Hackathon Registrations</h2>
        
        {/* Search and Stats */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search registrations..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: 6,
                width: 250,
                fontSize: '14px'
              }}
            />
            <span style={{ color: '#666', fontSize: '14px' }}>
              Showing {paginatedRegistrations.length} of {filteredRegistrations.length} registrations
            </span>
          </div>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}
      {error && <div style={{ color: 'red', padding: '10px', backgroundColor: '#ffebee', borderRadius: 4, marginBottom: 20 }}>{error}</div>}
      
      {!loading && !error && (
        <div style={{ background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {paginatedRegistrations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {filteredRegistrations.length === 0 && searchTerm 
                ? `No registrations found matching "${searchTerm}"`
                : 'No registrations found.'
              }
            </div>
          ) : (
            <>
              {/* Enhanced Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th onClick={() => handleSort('name')} style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', fontWeight: 600, color: '#333' }}>Full Name {getSortIcon('name')}</th>
                      <th onClick={() => handleSort('email')} style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', fontWeight: 600, color: '#333' }}>Email {getSortIcon('email')}</th>
                      <th onClick={() => handleSort('track')} style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', fontWeight: 600, color: '#333' }}>Track {getSortIcon('track')}</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#333' }}>Participation Type</th>
                      <th onClick={() => handleSort('phone')} style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', fontWeight: 600, color: '#333' }}>Phone {getSortIcon('phone')}</th>
                      <th onClick={() => handleSort('nationality')} style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', fontWeight: 600, color: '#333' }}>Nationality {getSortIcon('nationality')}</th>
                      <th onClick={() => handleSort('tshirt_size')} style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', fontWeight: 600, color: '#333' }}>T-Shirt {getSortIcon('tshirt_size')}</th>
                      <th onClick={() => handleSort('created_at')} style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', fontWeight: 600, color: '#333' }}>Registered {getSortIcon('created_at')}</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#333' }}>Team Members</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRegistrations.map((reg, index) => (
                      <tr 
                        key={reg.id} 
                        style={{ 
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e3f2fd'}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = index % 2 === 0 ? '#fafafa' : 'white';
                        }}
                      >
                        <td style={{ padding: '12px 16px', fontWeight: 500 }}>
                          {reg.name || reg.full_name || 'N/A'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#006400', fontWeight: 500 }}>
                          {reg.email}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {reg.track && (
                            <span style={{ 
                              padding: '4px 8px', 
                              borderRadius: 12, 
                              backgroundColor: '#e8f5e8',
                              color: '#2e7d32',
                              fontSize: '12px',
                              fontWeight: 600
                            }}>
                              {reg.track}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {reg.track === 'tech' ? (reg.team_type === 'team' ? 'Team' : reg.team_type === 'solo' ? 'Solo' : 'N/A') : 'N/A'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {reg.phone || 'N/A'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {reg.nationality || 'N/A'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {reg.tshirt_size && (
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: 12,
                              backgroundColor: '#fff3e0',
                              color: '#e65100',
                              fontSize: '12px',
                              fontWeight: 600
                            }}>
                              {reg.tshirt_size}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#666', fontSize: '14px' }}>
                          {reg.created_at ? new Date(reg.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                          {reg.track === 'tech' && reg.team_type === 'team' && reg.team_members && reg.team_members.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {reg.team_members.map((tm, i) => (
                                <li key={i}>
                                  <strong>{tm.full_name}</strong> ({tm.role})<br />
                                  <span style={{ color: '#555' }}>{tm.email}, {tm.phone}</span>
                                </li>
                              ))}
                            </ul>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  padding: '20px',
                  borderTop: '1px solid #f0f0f0',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      background: currentPage === 1 ? '#f5f5f5' : 'white',
                      color: currentPage === 1 ? '#999' : '#333',
                      borderRadius: 4,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Previous
                  </button>
                  
                  <span style={{ padding: '0 16px', color: '#666', fontSize: '14px' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      background: currentPage === totalPages ? '#f5f5f5' : 'white',
                      color: currentPage === totalPages ? '#999' : '#333',
                      borderRadius: 4,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
